import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const UserStatCardSkeleton = () => (
  <div className="col-xl-auto col-lg-auto col-md-3 mb-2 stat-card-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />

        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
      </div>
    </div>
  </div>
);

const User = () => {
  const { canCreate, canView, canUpdate, canDelete } =
    useModuleAccess("user_management");
  const savedFilters = JSON.parse(
    sessionStorage.getItem("userFilters") || "{}",
  );
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState("");
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    pendingUsers: 0,
  });
  const [roles, setRoles] = useState([]);
  const [roleFilter, setRoleFilter] = useState(savedFilters.roleFilter || "");
  const [activeRoleFilter, setActiveRoleFilter] = useState(
    savedFilters.activeRoleFilter || "",
  );

  const [isStatsLoading, setIsStatsLoading] = useState(true);

  // Fetch users with server-side pagination
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);

    try {
      const payload = {
        action: "get-users",
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        // status: "active"
      };

      // Add search parameter if activeSearchQuery has a value (not empty)
      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      if (activeRoleFilter) {
        payload.role = activeRoleFilter;
      }

      // Add sorting parameters if sortBy is set
      if (sortBy) {
        payload.sortBy = sortBy;
        payload.sortOrder = sortOrder;
      }

      const response = await axios.post(API_URL, payload);

      // Parse the response - handle both string and object responses
      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      // Check if request was successful
      if (responseData.success === true && responseData.users) {
        setUsers(responseData.users);
        setFilteredUsers(responseData.users);
        // Use pagination object if available, otherwise fallback to count or users length
        if (responseData.pagination && responseData.pagination.totalCount) {
          setTotalCount(responseData.pagination.totalCount);
        } else {
          setTotalCount(responseData.count || responseData.users.length);
        }
      } else {
        toast.error(responseData.message || "Failed to fetch users", {
          autoClose: 2000,
        });
        setUsers([]);
        setFilteredUsers([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch users error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message || "An error occurred while fetching users",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
      setUsers([]);
      setFilteredUsers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    activeRoleFilter,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = users.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };
  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
      itemsPerPage,
      sortBy,
      sortOrder,
      activeRoleFilter,
    };

    sessionStorage.setItem("userFilters", JSON.stringify(filters));
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    activeRoleFilter,
    sortBy,
    sortOrder,
  ]);

  const fetchUserStats = async () => {
    try {
      const payload = {
        action: "user-count",
      };

      const response = await axios.post(API_URL, payload);
      let responseData = response.data;

      if (typeof responseData === "string") {
        responseData = JSON.parse(responseData);
      }

      if (responseData.success && responseData.data) {
        setUserStats({
          totalUsers: responseData.data.totalCount || 0,
          activeUsers: responseData.data.active || 0,
          inactiveUsers: responseData.data.inactive || 0,
          pendingUsers: responseData.data.pending || 0,
        });
      }
    } catch (error) {
      console.error("User status count error:", error);

      setUserStats({
        totalUsers: 0,
        activeUsers: 0,
        inactiveUsers: 0,
        pendingUsers: 0,
      });
    } finally {
      setIsStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserStats();
  }, []);
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show up to 7 page numbers

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages with ellipsis
      if (currentPage <= 4) {
        // Show first pages
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show last pages
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show middle pages
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.post(API_URL, { action: "get-user-roles" });
      let responseData = response.data;
      if (typeof responseData === "string") {
        responseData = JSON.parse(responseData);
      }
      if (responseData.success && Array.isArray(responseData.roles)) {
        setRoles(responseData.roles);
      }
    } catch (error) {
      console.error("Fetch roles error:", error);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleRoleFilterChange = (e) => {
    const value = e.target.value;
    setRoleFilter(value);
    setActiveRoleFilter(value);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    setRoleFilter("");
    setActiveRoleFilter("");
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const hasActiveFilters = Boolean(activeSearchQuery || activeRoleFilter);

  // Handle search input change - if field becomes empty, automatically reset search
  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    // If user backspaces and field becomes empty, automatically reset search to show all records
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      // Field is now empty and there was an active search, so reset it
      setActiveSearchQuery("");
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle search trigger - on Enter key or search icon click
  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";

    // If search query is empty (user backspaced all characters), reset the active search
    // This will trigger API call without search parameter to show all records
    if (trimmedQuery === "") {
      // Clear the active search to reset filter
      setActiveSearchQuery("");
      // Reset to first page
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    } else {
      // Set the active search with trimmed query
      setActiveSearchQuery(trimmedQuery);
      // Reset to first page when searching
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    // Map display column names to API field names
    const columnMap = {
      sno: "id", // S.No maps to id for sorting
      name: "name",
      email: "email",
      role: "role",
      status: "status",
    };

    const apiField = columnMap[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("createdAt");
        setSortOrder("desc");
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };

  // Get sort indicator icon
  const getSortIcon = (column) => {
    const columnMap = {
      sno: "id",
      name: "name",
      email: "email",
      role: "role",
      status: "status",
    };
    const apiField = columnMap[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em", flexShrink: 0 }}
        />
      );
    }
    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em", flexShrink: 0 }}
      />
    );
  };

  // Handle delete button click
  const handleDeleteClick = (userId, userName) => {
    setDeleteUserId(userId);
    setDeleteUserName(userName);
    // Bootstrap modal will be triggered by data-toggle attribute
  };

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!deleteUserId) {
      return;
    }

    // Get currentUserId from localStorage
    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    setIsDeleting(true);

    try {
      const payload = {
        action: "delete-user",
        id: deleteUserId,
        currentUserId: currentUserId,
      };

      const response = await axios.post(API_URL, payload);

      // Parse the response - handle both string and object responses
      let responseData = response.data;
      if (typeof responseData === "string") {
        try {
          responseData = JSON.parse(responseData);
        } catch (parseError) {
          responseData = response.data;
        }
      }

      // Check if deletion was successful
      if (responseData.success === true) {
        toast.success(responseData.message || "User deleted successfully", {
          autoClose: 2000,
        });

        // Close modal
        if (window.$) {
          window.$("#modaldelete").modal("hide");
        }

        // Reset delete state
        setDeleteUserId(null);
        setDeleteUserName("");

        // Refresh user list
        // If current page becomes empty after deletion, go to previous page
        if (users.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchUsers();
        }
      } else {
        toast.error(responseData.message || "Failed to delete user", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Delete user error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message || "An error occurred while deleting user",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Capitalize first letter
  const capitalize = (str) => {
    if (!str) return "-";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Users</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <Link to="/UserAdd" className="btn btn-blue">
                  Invite User
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row stat-card-row">
                {isStatsLoading ? (
                  <>
                    <UserStatCardSkeleton />
                    <UserStatCardSkeleton />
                    <UserStatCardSkeleton />
                    <UserStatCardSkeleton />
                  </>
                ) : (
                  <>
                    {/* TOTAL USERS */}

                    <div className="col-xl-auto col-lg-auto col-md-3 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: "#127aea",
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Total Users
                          </div>

                          <div
                            className="stat-value"
                            style={{
                              color: "#2c7fd8",
                              fontSize: "22px",
                              fontWeight: "700",
                            }}
                          >
                            {userStats.totalUsers}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ACTIVE */}

                    <div className="col-xl-auto col-lg-auto col-md-3 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: "#196F3D",
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Active
                          </div>

                          <div
                            className="stat-value"
                            style={{
                              color: "#27AE60",
                              fontSize: "22px",
                              fontWeight: "700",
                            }}
                          >
                            {userStats.activeUsers}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-auto col-lg-auto col-md-3 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: "#7D6608",
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Pending
                          </div>

                          <div
                            className="stat-value"
                            style={{
                              color: "#F39C12",
                              fontSize: "22px",
                              fontWeight: "700",
                            }}
                          >
                            {userStats.pendingUsers}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* INACTIVE */}

                    <div className="col-xl-auto col-lg-auto col-md-3 mb-2 stat-card-col">
                      <div className="card supplier-stat-card">
                        <div className="card-body">
                          <div
                            className="stat-title"
                            style={{
                              color: "#C0392B",
                              fontSize: "13px",
                              fontWeight: "600",
                              marginBottom: "8px",
                            }}
                          >
                            Inactive
                          </div>

                          <div
                            className="stat-value"
                            style={{
                              color: "#E74C3C",
                              fontSize: "22px",
                              fontWeight: "700",
                            }}
                          >
                            {userStats.inactiveUsers}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <div className="form-group mb-0">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search users..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <div className="input-group-append">
                          <div
                            className="input-group-text crsr_pntr"
                            onClick={handleSearchTrigger}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa-solid fa-search"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <div className="form-group mb-0">
                      <select
                        className="form-control"
                        value={roleFilter}
                        onChange={handleRoleFilterChange}
                      >
                        <option value="">Select Roles</option>
                        {roles.map((r) => (
                          <option key={r} value={r}>
                            {r.charAt(0).toUpperCase() + r.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {hasActiveFilters && (
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleClearFilters}
                      >
                        Clear Filter
                      </button>
                    </div>
                  )}

                  <div className="col-md-3 text-right">
                    <div className="form-group mb-0">
                      <p className="mb-0">
                        {hasActiveFilters && (
                          <span className="ml-2 text-muted">
                            (Showing: {filteredUsers.length})
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : users.length === 0 ? (
                <div className="nocontent_div text-center p-4">
                  There are no users yet.
                  <br />
                  {canCreate && (
                    <div className="mt-3">
                      <Link to="/UserAdd" className="btn btn-blue">
                        Invite User
                      </Link>
                    </div>
                  )}
                </div>
              ) : filteredUsers.length === 0 && activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  No users found matching your search.
                  <br />
                  <div className="mt-3">
                    <button
                      className="btn btn-blue"
                      onClick={handleClearFilters}
                    >
                      Clear Filter
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="users"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("sno")}
                            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              S.No {getSortIcon("sno")}
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("name")}
                            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Name {getSortIcon("name")}
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("email")}
                            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Email {getSortIcon("email")}
                            </span>
                          </th>
                          <th>Phone</th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("role")}
                            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Role {getSortIcon("role")}
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("status")}
                            style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Status {getSortIcon("status")}
                            </span>
                          </th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((user, index) => (
                          <tr key={user.id}>
                            <td>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td>{user.name || "-"}</td>
                            <td>{user.email || "-"}</td>
                            <td>{user.phoneNumber || "-"}</td>
                            <td>{capitalize(user.role) || "-"}</td>
                            <td>
                              <span
                                className={`fwt_600 ${user.status === "active" ? "f_green" : "f_red"}`}
                              >
                                {capitalize(user.status)}
                              </span>
                            </td>
                            <td>
                              <div align="center">
                                {canView && (
                                  <Link
                                    to={`/userView/${user.id}`}
                                    className="crsr_pntr"
                                  >
                                    <i className="fa-solid fa-eye mr-2" />
                                  </Link>
                                )}
                                {canUpdate && (
                                  <Link
                                    to={`/userEdit/${user.id}`}
                                    className="crsr_pntr"
                                  >
                                    <i className="fa-solid fa-pen mr-2" />
                                  </Link>
                                )}
                                {canDelete && (
                                  <a
                                    href="#modaldelete"
                                    className="crsr_pntr action-icon-link"
                                    data-toggle="modal"
                                    data-target="#modaldelete"
                                    title="Delete"
                                    onClick={() =>
                                      handleDeleteClick(user.id, user.name)
                                    }
                                  >
                                    <i className="fa-solid fa-trash-can" />
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination with Items Per Page Dropdown */}
                  {!isLoading && users.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Delete User
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete <br />
                <span className="fwt_600">{deleteUserName}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default User;
